import type { MapId, ScenarioId } from "../domain/types";
import generatedScenarios from "./wiki-scenarios.generated.json";

export interface ScenarioDefinition {
    readonly id: ScenarioId;
    readonly name: string;
    readonly mapIds: readonly MapId[];
}

interface GeneratedScenarioDefinition {
    readonly id: string;
    readonly name: string;
    readonly mapIds: readonly string[];
}

const VALID_SCENARIO_IDS = new Set<ScenarioId>([
    "sandbox",
    "fj1_garage_to_glory",
    "scenario_arm_race",
    "scenario_garage_to_glory_hard",
]);
const VALID_MAP_IDS = new Set<MapId>(["garage", "basement", "small-colo", "small-campus"]);

function readScenario(source: GeneratedScenarioDefinition): ScenarioDefinition {
    if (!VALID_SCENARIO_IDS.has(source.id as ScenarioId)) {
        throw new Error(`Unknown generated scenario ID: ${source.id}.`);
    }
    if (source.name.trim() === "" || source.mapIds.some((mapId) => !VALID_MAP_IDS.has(mapId as MapId))) {
        throw new Error(`Generated scenario "${source.id}" is invalid.`);
    }
    return Object.freeze({
        id: source.id as ScenarioId,
        name: source.name,
        mapIds: Object.freeze(source.mapIds as MapId[]),
    });
}

export const SCENARIOS: readonly ScenarioDefinition[] = Object.freeze(
    (generatedScenarios as readonly GeneratedScenarioDefinition[]).map(readScenario),
);

export function getScenarios(mapId?: MapId): readonly ScenarioDefinition[] {
    return mapId === undefined ? SCENARIOS : SCENARIOS.filter((scenario) => scenario.mapIds.includes(mapId));
}

export function getScenario(scenarioId: string): ScenarioDefinition | undefined {
    return SCENARIOS.find((scenario) => scenario.id === scenarioId);
}

export function getDefaultScenarioId(): ScenarioId {
    return getScenario("fj1_garage_to_glory")?.id ?? "sandbox";
}
